import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ExactArrayItems } from "@Helpers/types/exact";
import type {
  FileManagerLocation,
  FileManagerRow,
  ListingSpace,
} from "@CubeComponents/FileManager/fileSystemServer.schema";

export type { FileManagerLocation, FileManagerRow, ListingSpace };

const refreshFileManagerSlice = createSlice({
  name: "refreshFileManager",
  initialState: false,
  reducers: {
    setRefreshFileManager: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

/** The rows selected in the file manager, or the file the properties panel was opened on. */
const selectedFilesSlice = createSlice({
  name: "selectedFiles",
  initialState: [] as FileManagerRow[],
  reducers: {
    setSelectedFiles: (_state: FileManagerRow[], action: PayloadAction<FileManagerRow[]>) => action.payload,
    emptySelectedFiles: (): FileManagerRow[] => [],
  },
});

/** Where the main file manager stands; `null` until its first read lands and after it unmounts. */
const currentFolderSlice = createSlice({
  name: "currentFolder",
  initialState: null as FileManagerLocation | null,
  reducers: {
    setCurrentFolder: (_state: FileManagerLocation | null, action: PayloadAction<FileManagerLocation | null>) =>
      action.payload,
    emptyCurrentFolder: () => null,
  },
});

/**
 * The project ("Folders") file manager's path, published for the app-header breadcrumb: the top of the Space, the
 * folders above the selected one, and the selected folder.
 */
const fileManagerBreadCrumbSlice = createSlice({
  name: "fileManagerBreadCrumb",
  initialState: [] as FileManagerLocation[],
  reducers: {
    setFileManagerBreadCrumbData: (_state: FileManagerLocation[], action: PayloadAction<FileManagerLocation[]>) =>
      action.payload,
  },
});

export const { setRefreshFileManager } = refreshFileManagerSlice.actions;

const { setSelectedFiles: _setSelectedFilesRaw } = selectedFilesSlice.actions;
export const { setFileManagerBreadCrumbData } = fileManagerBreadCrumbSlice.actions;
export const { setCurrentFolder, emptyCurrentFolder } = currentFolderSlice.actions;
export const { emptySelectedFiles } = selectedFilesSlice.actions;

/**
 * The array-payload action creator wrapped with `ExactArrayItems<>` so excess properties are caught even when the
 * payload comes through a variable. Array wraps run per-element checking.
 */
export const setSelectedFiles = <T extends readonly FileManagerRow[]>(
  payload: T & ExactArrayItems<FileManagerRow, T>,
) => _setSelectedFilesRaw([...payload]);

export const refreshFileManager = refreshFileManagerSlice.reducer;
export const selectedFiles = selectedFilesSlice.reducer;
export const currentFolder = currentFolderSlice.reducer;
export const fileManagerBreadCrumb = fileManagerBreadCrumbSlice.reducer;
